import React, { useState } from 'react';
import { ChevronDownIcon, PackageSearchIcon, SlidersHorizontalIcon } from 'lucide-react';
import type { CategoryId } from '../../types/product';
import { FilterPanel } from './FilterPanel';
import { ActiveFilters } from './ActiveFilters';
import { MobileFilterDrawer } from './MobileFilterDrawer';
import { ProductCard } from '../product/ProductCard';
import { Breadcrumbs } from '../layout/Breadcrumbs';
import { useProductFilters, type SortId } from '../../hooks/useProductFilters';
import { categories } from '../../data/categories';
import { sortOptions } from '../../data/filters';
export function ShopCatalog({
  category,
  deals
}: {
  category?: CategoryId;
  deals: boolean;
}) {
  const api = useProductFilters(category, deals);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const cat = categories.find(c => c.id === category);
  const title = deals ? 'Aanbiedingen' : cat?.name ?? 'Alle producten';
  const subtitle = deals ? 'Tijdelijke acties op thuisbatterijen, powerstations en zonnepanelen.' : cat ? `${cat.description}. Inclusief gratis bezorging en deskundig advies.` : 'Thuisbatterijen, powerstations, zonnepanelen en alles daartussen.';
  return <div className="mx-auto max-w-7xl px-4 pb-16 pt-5 sm:px-6 lg:px-8" data-id="element-656" data-mp="1m4zhiugccd5c">
      <Breadcrumbs items={[{
      label: 'Home',
      href: '/'
    }, ...(cat || deals ? [{
      label: 'Assortiment',
      href: '/shop'
    }] : []), {
      label: title
    }]} data-id="element-657" data-mp="1r4u9z2frlasi" />

      <div className="mt-4 border-b border-line pb-6" data-id="element-658" data-mp="1dc4metkl7xta">
        <h1 className={`text-[28px] font-bold tracking-tight sm:text-4xl ${deals ? 'text-sale' : 'text-ink'}`} data-id="element-659" data-mp="1i45x4b23xgh7">{title}</h1>
        <p className="mt-2 max-w-xl text-sm text-muted" data-id="element-660" data-mp="13gksudthxb7p">{subtitle}</p>
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-[240px_1fr] lg:gap-10" data-id="element-661" data-mp="18e344auzk2iv">
        <aside className="hidden lg:block" aria-label="Filters" data-id="element-662" data-mp="uetmr7v1n2db">
          <div className="sticky top-40 max-h-[calc(100vh-11rem)] overflow-y-auto pb-6 pr-2" data-id="element-663" data-mp="ufmxsl11ce19">
            <FilterPanel api={api} data-id="element-664" data-mp="yfx4y10kdahp" />
          </div>
        </aside>

        <div data-id="element-665" data-mp="313dh4clfw99l">
          <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-666" data-mp="313vnw35qa5vr">
            <div className="flex items-center gap-3" data-id="element-667" data-mp="2wc3u7jr9zk1e">
              <button type="button" onClick={() => setDrawerOpen(true)} className="inline-flex h-10 items-center gap-2 rounded-lg border border-line px-3.5 text-sm font-semibold text-ink hover:border-ink/40 lg:hidden" data-id="element-668" data-mp="2waan63cr2xsv">
                <SlidersHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-669" data-mp="3k16mdljjcva5" />
                Filters
                {api.activeCount > 0 && <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-ink px-1.5 text-[11px] text-white" data-id="element-670" data-mp="3a38yx09i32il">{api.activeCount}</span>}
              </button>
              <p className="text-sm text-muted" aria-live="polite" data-id="element-671" data-mp="1yp4u0z1djtyr">
                <span className="font-semibold text-ink" data-id="element-672" data-mp="1yma9l7id295c">{api.results.length}</span> producten
              </p>
            </div>
            <label className="relative flex items-center gap-2 text-sm text-muted" data-id="element-673" data-mp="a58zxqmwnzpi">
              <span className="hidden sm:inline" data-id="element-674" data-mp="e6kl9jyqxyrw">Sorteer op</span>
              <select value={api.sort} onChange={e => api.setSort(e.target.value as SortId)} className="h-10 appearance-none rounded-lg border border-line bg-white pl-3 pr-9 text-sm font-medium text-ink focus:border-ink focus:outline-none" data-id="element-675" data-mp="e6ucfavj2bz6">
                {sortOptions.map(o => <option key={o.id} value={o.id} data-id="element-676" data-mp="xu21q3q7l1s1">
                    {o.label}
                  </option>)}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-3 h-4 w-4 text-muted" aria-hidden="true" data-id="element-677" data-mp="11wydpbl8tzn0" />
            </label>
          </div>

          <div className="mt-4" data-id="element-678" data-mp="33o1u6hhte8fh">
            <ActiveFilters api={api} data-id="element-679" data-mp="33p342in3govm" />
          </div>

          {api.results.length === 0 ? <div className="mt-6 flex flex-col items-center rounded-2xl bg-surface px-6 py-16 text-center" data-id="element-680" data-mp="38n4whu6eu0jr">
              <PackageSearchIcon className="h-10 w-10 text-ink/25" aria-hidden="true" data-id="element-681" data-mp="2dalligxea9pi" />
              <p className="mt-4 text-base font-semibold text-ink" data-id="element-682" data-mp="28ctudp4kqrmd">Geen producten gevonden met deze filters</p>
              <p className="mt-1 text-sm text-muted" data-id="element-683" data-mp="2n9un9vyi4smf">Verwijder een filter of bekijk alle producten.</p>
              <button type="button" onClick={api.clearAll} className="mt-6 h-11 rounded-lg bg-ink px-5 text-sm font-semibold text-white hover:bg-ink-soft" data-id="element-684" data-mp="2icavdt8vshc0">
                Alle filters wissen
              </button>
            </div> : <ul className="mt-5 grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-3 xl:gap-5" data-id="element-685" data-mp="24c9zytbdy23x">
              {api.results.map(p => <li key={p.id} data-id="element-686" data-mp="1zjgsvysg2hoo">
                  <ProductCard product={p} data-id="element-687" data-mp="1zj6zs2c3ruer" />
                </li>)}
            </ul>}
        </div>
      </div>

      <MobileFilterDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} api={api} data-id="element-688" data-mp="16wvz6rohxthy" />
    </div>;
}